import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import type { ApplyDepreciationPresetOutput, DepreciationPreset, PresetGradeRates } from 'used-vehicle-contracts';
import { orpc } from '../../apis/orpc-query';
import { useAuth } from '../../auth/AuthContext';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { Dialog } from '../../components/ui/Dialog';
import { FormField, FormSection } from '../../components/ui/FormField';
import { Input } from '../../components/ui/Input';
import { formatDay } from '../../lib/format';
import { isConflictError, serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { PercentInput } from './PercentInput';
import { DAMAGE_GROUP_SIZE, fromPercentInput, toPercentDisplay } from './templates-display';

const GRADES = ['G7', 'G4', 'G3', 'G2', 'G1', 'G0'] as const;

interface PresetDraft {
  rates: string[];
  grades: Record<(typeof GRADES)[number], string>;
  damage1: string[];
  damage2: string[];
}

const draftOf = (preset: DepreciationPreset): PresetDraft => ({
  rates: preset.rates.map((rate) => String(toPercentDisplay(rate))),
  grades: Object.fromEntries(GRADES.map((grade) => [grade, String(toPercentDisplay(preset.gradeRates[grade]))])) as PresetDraft['grades'],
  damage1: preset.damageGroup1.map(String),
  damage2: preset.damageGroup2.map(String),
});

const parse = (raw: string): number | null => {
  const trimmed = raw.trim();
  const value = trimmed === '' ? Number.NaN : Number(trimmed);

  return Number.isFinite(value) && value >= 0 ? value : null;
};

type ApplyScope = 'ALL' | 'SELECTED';

/**
 * Settings for the starter presets themselves (business review 2026-09-23, B2; plan §11.19).
 *
 * Every member sees the four presets and their values; an administrator edits one — the four
 * depreciation years, the six grade deductions and both damage tables — and is then asked where the new
 * values should go: into **every** active template, into **chosen** templates, or nowhere yet. Applying
 * writes the values into each template's **draft** (opening one from the published version when there is
 * none); publishing each draft stays the explicit step it always was, and the dialog says so.
 */
export const StarterPresetsCard = () => {
  const { t } = useI18nTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'ADMIN';

  const presetsQuery = useQuery(orpc.templates.depreciationPresets.queryOptions());
  const [editing, setEditing] = useState<DepreciationPreset | null>(null);
  const [draft, setDraft] = useState<PresetDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState<DepreciationPreset | null>(null);
  const [scope, setScope] = useState<ApplyScope>('ALL');
  const [chosen, setChosen] = useState<string[]>([]);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyDepreciationPresetOutput | null>(null);

  const templatesQuery = useQuery(
    orpc.templates.list.queryOptions({
      input: { page: 1, pageSize: 100, sortBy: 'name', sortDirection: 'asc', includeArchived: false },
      enabled: applying !== null,
    }),
  );

  const updatePreset = useMutation(orpc.templates.updateDepreciationPreset.mutationOptions());
  const applyPreset = useMutation(orpc.templates.applyDepreciationPreset.mutationOptions());

  const presets = presetsQuery.data ?? [];

  const openEditor = (preset: DepreciationPreset): void => {
    setEditing(preset);
    setDraft(draftOf(preset));
    setError(null);
  };

  const save = async (): Promise<void> => {
    if (editing === null || draft === null) {
      return;
    }

    const rates = draft.rates.map(parse);
    const grades = GRADES.map((grade) => parse(draft.grades[grade]));
    const damage1 = draft.damage1.map(parse);
    const damage2 = draft.damage2.map(parse);

    if ([...rates, ...grades, ...damage1, ...damage2].some((value) => value === null)) {
      setError(t('templates.presetValuesInvalid'));

      return;
    }

    try {
      const saved = await updatePreset.mutateAsync({
        key: editing.key,
        expectedRevision: editing.revision,
        rates: rates.map((rate) => fromPercentInput(rate!)),
        gradeRates: Object.fromEntries(GRADES.map((grade, index) => [grade, fromPercentInput(grades[index]!)])) as PresetGradeRates,
        damageGroup1: damage1 as number[],
        damageGroup2: damage2 as number[],
      });

      await queryClient.invalidateQueries({ queryKey: orpc.templates.depreciationPresets.key() });
      setEditing(null);
      setDraft(null);
      // The question the review asked for: where should the new values go?
      setApplying(saved);
      setScope('ALL');
      setChosen([]);
      setApplyError(null);
      setResult(null);
    } catch (caught: unknown) {
      if (isConflictError(caught)) {
        await presetsQuery.refetch();
      }

      setError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const apply = async (): Promise<void> => {
    if (applying === null) {
      return;
    }

    setApplyError(null);

    try {
      const output = await applyPreset.mutateAsync({
        key: applying.key,
        scope,
        ...(scope === 'SELECTED' ? { templateIds: chosen } : {}),
        reason: t('templates.presetApplyReason', { preset: t(`templates.presets.${applying.key}`, applying.label) }),
      });

      setResult(output);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: orpc.templates.list.key() }),
        queryClient.invalidateQueries({ queryKey: orpc.templates.get.key() }),
      ]);
    } catch (caught: unknown) {
      setApplyError(serverMessage(caught) ?? t(submitErrorKey(caught)));
    }
  };

  const closeApply = (): void => {
    setApplying(null);
    setResult(null);
  };

  const percentList = (values: readonly number[]): string => values.map((value) => `${toPercentDisplay(value)}%`).join(' · ');

  return (
    <section
      className="space-y-3 rounded-lg border border-border bg-card p-5"
      aria-labelledby="starter-presets-title"
    >
      <div>
        <h2
          id="starter-presets-title"
          className="text-sm font-semibold text-card-foreground"
        >
          {t('templates.presetsTitle')}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('templates.presetsBody')}</p>
      </div>

      <ul className="divide-y divide-border">
        {presets.map((preset) => (
          <li
            key={preset.key}
            className="flex flex-wrap items-center justify-between gap-3 py-2.5"
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{t(`templates.presets.${preset.key}`, preset.label)}</p>
              <p className="text-xs text-muted-foreground">
                {t('templates.presetYears')}{' '}
                <span
                  dir="ltr"
                  className="tabular-nums"
                >
                  {percentList(preset.rates)}
                </span>
              </p>
              <p className="text-xs text-muted-foreground">
                {preset.updatedAt === null
                  ? t('templates.presetFactory')
                  : t('templates.presetEdited', { name: preset.updatedByName ?? t('common.notRecorded'), date: formatDay(preset.updatedAt) })}
              </p>
            </div>
            {isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => openEditor(preset)}
              >
                <Pencil className="size-3.5" />
                {t('templates.presetEdit')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      <Dialog
        isOpen={editing !== null && draft !== null}
        onClose={() => setEditing(null)}
        title={t('templates.presetEditTitle', { preset: editing ? t(`templates.presets.${editing.key}`, editing.label) : '' })}
        description={t('templates.presetEditBody')}
        className="max-w-2xl"
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => setEditing(null)}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              isLoading={updatePreset.isPending}
              onClick={() => void save()}
            >
              {t('templates.presetSave')}
            </Button>
          </>
        }
      >
        {draft === null ? null : (
          <div className="max-h-[60vh] space-y-5 overflow-y-auto pe-1">
            <FormSection title={t('paramGroups.DEPRECIATION')}>
              <div className="grid gap-3 sm:grid-cols-2">
                {draft.rates.map((rate, index) => (
                  <FormField
                    key={`rate-${index}`}
                    id={`preset-rate-${index}`}
                    label={t(`params.depYear${index + 1}`)}
                    required
                  >
                    <PercentInput
                      id={`preset-rate-${index}`}
                      value={rate}
                      onValueChange={(next) =>
                        setDraft((current) => current && { ...current, rates: current.rates.map((entry, at) => (at === index ? next : entry)) })
                      }
                    />
                  </FormField>
                ))}
              </div>
            </FormSection>
            <FormSection title={t('templates.presetGrades')}>
              <div className="grid gap-3 sm:grid-cols-3">
                {GRADES.map((grade) => (
                  <FormField
                    key={grade}
                    id={`preset-grade-${grade}`}
                    label={t(`params.grade${grade}`)}
                    required
                  >
                    <PercentInput
                      id={`preset-grade-${grade}`}
                      value={draft.grades[grade]}
                      onValueChange={(next) => setDraft((current) => current && { ...current, grades: { ...current.grades, [grade]: next } })}
                    />
                  </FormField>
                ))}
              </div>
            </FormSection>
            {(['damage1', 'damage2'] as const).map((table, tableIndex) => (
              <FormSection
                key={table}
                title={t(`templates.damageGroup${tableIndex + 1}`)}
              >
                <div className="grid gap-3 sm:grid-cols-4">
                  {Array.from({ length: DAMAGE_GROUP_SIZE }, (_, index) => (
                    <FormField
                      key={`${table}-${index}`}
                      id={`preset-${table}-${index}`}
                      label={t('templates.presetDamageRow', { row: index })}
                      required
                    >
                      <Input
                        id={`preset-${table}-${index}`}
                        type="number"
                        dir="ltr"
                        min={0}
                        step="any"
                        className="tabular-nums"
                        value={draft[table][index] ?? ''}
                        onChange={(event) =>
                          setDraft(
                            (current) =>
                              current && { ...current, [table]: current[table].map((entry, at) => (at === index ? event.target.value : entry)) },
                          )
                        }
                      />
                    </FormField>
                  ))}
                </div>
              </FormSection>
            ))}
            {error === null ? null : (
              <p
                role="alert"
                className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        isOpen={applying !== null}
        onClose={closeApply}
        title={t('templates.presetApplyTitle', { preset: applying ? t(`templates.presets.${applying.key}`, applying.label) : '' })}
        description={t('templates.presetApplyBody')}
        footer={
          result === null ? (
            <>
              <Button
                variant="outline"
                onClick={closeApply}
              >
                {t('templates.presetApplyLater')}
              </Button>
              <Button
                isLoading={applyPreset.isPending}
                disabled={scope === 'SELECTED' && chosen.length === 0}
                onClick={() => void apply()}
              >
                {scope === 'ALL' ? t('templates.presetApplyAll') : t('templates.presetApplySelected', { count: chosen.length })}
              </Button>
            </>
          ) : (
            <Button onClick={closeApply}>{t('actions.close')}</Button>
          )
        }
      >
        {result === null ? (
          <div className="space-y-3">
            <fieldset className="space-y-2">
              <legend className="sr-only">{t('templates.presetApplyScope')}</legend>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="preset-scope"
                  checked={scope === 'ALL'}
                  onChange={() => setScope('ALL')}
                />
                {t('templates.presetScopeAll')}
              </label>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="radio"
                  name="preset-scope"
                  checked={scope === 'SELECTED'}
                  onChange={() => setScope('SELECTED')}
                />
                {t('templates.presetScopeSelected')}
              </label>
            </fieldset>
            {scope === 'SELECTED' ? (
              <div className="max-h-56 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                {(templatesQuery.data?.items ?? []).map((template) => (
                  <div key={template.id}>
                    <Checkbox
                      id={`preset-target-${template.id}`}
                      label={`${template.name} · ${template.make} ${template.model}`}
                      checked={chosen.includes(template.id)}
                      onChange={(event) =>
                        setChosen((current) => (event.target.checked ? [...current, template.id] : current.filter((id) => id !== template.id)))
                      }
                    />
                  </div>
                ))}
              </div>
            ) : null}
            <p className="rounded-md border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">{t('templates.presetApplyDraftNote')}</p>
            {applyError === null ? null : (
              <p
                role="alert"
                className="text-sm text-destructive"
              >
                {applyError}
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            <p className="text-foreground">{t('templates.presetApplied', { count: result.applied.length })}</p>
            <ul className="list-inside list-disc text-muted-foreground">
              {result.applied.map((row) => (
                <li key={row.templateId}>
                  {t(row.createdDraft ? 'templates.presetAppliedNewDraft' : 'templates.presetAppliedDraft', {
                    name: row.templateName,
                    version: row.draftVersion,
                  })}
                </li>
              ))}
            </ul>
            {result.skipped.length === 0 ? null : (
              <>
                <p className="text-foreground">{t('templates.presetSkipped', { count: result.skipped.length })}</p>
                <ul className="list-inside list-disc text-muted-foreground">
                  {result.skipped.map((row) => (
                    <li key={row.templateId}>
                      {row.templateName} — {t(row.reasonKey)}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="text-xs text-muted-foreground">{t('templates.presetApplyDraftNote')}</p>
          </div>
        )}
      </Dialog>
    </section>
  );
};
